import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static values = {
  url: String,
  column: String,
  slug: String,
  page: Number,
  filter: String,
  showResolved: { type: String, default: "" },
  params: { type: Object, default: {} }
  }

  connect() {
    this.observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        this.loadMore()
      }
    }, { rootMargin: '200px' })

    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer.disconnect()
  }

  async loadMore() {
    this.observer.disconnect()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      
      // Preserve existing query parameters
      const currentParams = new URLSearchParams(window.location.search)
      for (const [key, value] of currentParams) {
        url.searchParams.set(key, value)
      }

      // Explicitly set show_resolved from data attribute (URL may not reflect it)
      if (this.showResolvedValue) {
        url.searchParams.set('show_resolved', this.showResolvedValue)
      } else {
        url.searchParams.delete('show_resolved')
      }
      
      // Apply the server-rendered filter params last so they override any stale
      // or missing values in window.location.search (filters applied via the
      // Turbo Frame form do not update the browser URL).
      for (const [key, value] of Object.entries(this.paramsValue)) {
        if (value === null || value === undefined || value === "") {
          url.searchParams.delete(key)
        } else {
          url.searchParams.set(key, value)
        }
      }

      // Ensure column_filter is explicitly set from data attribute
      url.searchParams.set('column_filter', this.filterValue)
      
      // Update with pagination parameters
      url.searchParams.set('column', this.columnValue)
      url.searchParams.set('column_page', this.pageValue)
      if (!url.searchParams.get('view')) {
        url.searchParams.set('view', 'board')
      }

      const response = await window.fetch(url.toString(), {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        console.error("Column loader fetch failed", response.status)
        return
      }

      const text = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(text, 'text/html')

      // Use the pre-computed slug from Rails — no JS string conversion needed
      const slug = this.slugValue
      const itemsId = `board_status_${slug}_items`
      const sentinelId = `board_col_sentinel_${slug}`

      let streamHtml = ""

      const newItems = doc.getElementById(itemsId)
      if (newItems && newItems.children.length > 0) {
        streamHtml += `<turbo-stream action="append" target="${itemsId}"><template>`
        streamHtml += newItems.innerHTML
        streamHtml += `</template></turbo-stream>`

        streamHtml += `<turbo-stream action="remove" target="board_status_${slug}_empty"></turbo-stream>`
      }

      const nextSentinel = doc.getElementById(sentinelId)
      if (nextSentinel) {
        streamHtml += `<turbo-stream action="replace" target="${sentinelId}"><template>`
        streamHtml += nextSentinel.outerHTML
        streamHtml += `</template></turbo-stream>`
      } else {
        streamHtml += `<turbo-stream action="remove" target="${sentinelId}"></turbo-stream>`
      }

      if (streamHtml !== "") {
        Turbo.renderStreamMessage(streamHtml)
      }

    } catch (err) {
      console.error("Column loader error", err)
    }
  }
}